import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import { sendClientOtp, verifyClientOtp, devLoginClient } from "../../api/authService";
import { AppPromoContent, wasPromoShownThisSession, markPromoShown } from "./AppPromoModal";
import "./ClientLoginModal.css";

const RESEND_COOLDOWN_SECONDS = 60;
const OTP_LENGTH = 6;
const EMPTY_OTP = Array(OTP_LENGTH).fill("");

const PHONE_OPTIONS = [
  { id: "IL-972", country: "IL", callingCode: "972", labelKey: "countryIsrael" },
  { id: "PS-972", country: "PS", callingCode: "972", labelKey: "countryPalestine" },
  { id: "PS-970", country: "PS", callingCode: "970", labelKey: "countryPalestine" },
];

// Inline SVG because flag emojis don't render on Windows.
function CountryFlag({ country }) {
  if (country === "IL") {
    return (
      <svg className="client-login-flag" viewBox="0 0 22 16" aria-hidden="true">
        <rect width="22" height="16" fill="#fff" />
        <rect y="1.5" width="22" height="2.5" fill="#0038b8" />
        <rect y="12" width="22" height="2.5" fill="#0038b8" />
        <path d="M11 4.6l2.6 4.5H8.4zM11 11.4L8.4 6.9h5.2z" fill="none" stroke="#0038b8" strokeWidth="0.8" />
      </svg>
    );
  }
  return (
    <svg className="client-login-flag" viewBox="0 0 22 16" aria-hidden="true">
      <rect width="22" height="5.34" fill="#000" />
      <rect y="5.33" width="22" height="5.34" fill="#fff" />
      <rect y="10.66" width="22" height="5.34" fill="#007a3d" />
      <path d="M0 0l9 8-9 8z" fill="#ce1126" />
    </svg>
  );
}

const isDevEnvironment = process.env.NODE_ENV !== "production";

function getErrorMessage(err, fallback) {
  const data = err?.response?.data;
  if (typeof data === "string") return data;
  if (data?.message) return data.message;
  if (data?.title) return data.title;
  return fallback;
}

export default function ClientLoginModal({ onClose }) {
  const { t } = useTranslation(["clientLoginModal", "common"]);
  const { setSession } = useAuth();

  const [step, setStep] = useState("phone");
  const [phoneOptionId, setPhoneOptionId] = useState(PHONE_OPTIONS[0].id);
  const [localNumber, setLocalNumber] = useState("");
  const [otpDigits, setOtpDigits] = useState(EMPTY_OTP);
  const [loading, setLoading] = useState(false);
  const [devLoading, setDevLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [error, setError] = useState("");
  const otpRefs = useRef([]);
  // Before registering, pitch the app (12% coupon) once per session.
  const [showPromo, setShowPromo] = useState(() => !wasPromoShownThisSession());

  useEffect(() => {
    if (showPromo) markPromoShown();
  }, [showPromo]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => {
      setCooldown((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  useEffect(() => {
    function handleKeyDown(e) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  const { callingCode, country } = PHONE_OPTIONS.find((option) => option.id === phoneOptionId);
  const fullPhone = `+${callingCode}${localNumber.replace(/^0+/, "")}`;
  const code = otpDigits.join("");
  const isPhoneValid = localNumber.trim().length >= 7;

  function focusOtpBox(index) {
    setTimeout(() => otpRefs.current[index]?.focus(), 50);
  }

  async function sendOtp(e) {
    e?.preventDefault();
    if (!isPhoneValid) {
      setError(t("invalidPhoneMessage"));
      return;
    }

    setError("");
    setLoading(true);
    try {
      await sendClientOtp(fullPhone, country);
      setStep("otp");
      setOtpDigits(EMPTY_OTP);
      setCooldown(RESEND_COOLDOWN_SECONDS);
      focusOtpBox(0);
    } catch (err) {
      if (err?.response?.status === 429) {
        const retryAfterSeconds = err.response.data?.retryAfterSeconds ?? RESEND_COOLDOWN_SECONDS;
        setStep("otp");
        setCooldown(retryAfterSeconds);
      } else {
        setError(getErrorMessage(err, t("sendCodeFallbackError")));
      }
    } finally {
      setLoading(false);
    }
  }

  async function verifyOtp(finalCode) {
    const codeToVerify = finalCode ?? code;
    if (codeToVerify.length < OTP_LENGTH) {
      setError(t("invalidCodeMessage"));
      return;
    }

    setError("");
    setLoading(true);
    try {
      const data = await verifyClientOtp(fullPhone, codeToVerify);
      setSession(data);
      onClose();
    } catch (err) {
      setError(getErrorMessage(err, t("verifyFailedMessage")));
      setOtpDigits(EMPTY_OTP);
      focusOtpBox(0);
    } finally {
      setLoading(false);
    }
  }

  async function continueAsDev() {
    setError("");
    setDevLoading(true);
    try {
      const data = await devLoginClient();
      setSession(data);
      onClose();
    } catch (err) {
      setError(getErrorMessage(err, t("devLoginFailedMessage")));
    } finally {
      setDevLoading(false);
    }
  }

  function backToPhone() {
    setStep("phone");
    setOtpDigits(EMPTY_OTP);
    setError("");
  }

  function handleOtpChange(index, rawValue) {
    const digits = rawValue.replace(/\D/g, "");

    if (!digits) {
      setOtpDigits((prev) => {
        const next = [...prev];
        next[index] = "";
        return next;
      });
      return;
    }

    if (digits.length > 1) {
      const next = [...otpDigits];
      let lastIndex = index;
      for (let i = 0; i < digits.length && index + i < OTP_LENGTH; i++) {
        next[index + i] = digits[i];
        lastIndex = index + i;
      }
      setOtpDigits(next);

      const joined = next.join("");
      if (joined.length >= OTP_LENGTH) {
        verifyOtp(joined);
      } else {
        focusOtpBox(Math.min(lastIndex + 1, OTP_LENGTH - 1));
      }
      return;
    }

    const next = [...otpDigits];
    next[index] = digits;
    setOtpDigits(next);

    if (index < OTP_LENGTH - 1) {
      focusOtpBox(index + 1);
    } else if (next.join("").length >= OTP_LENGTH) {
      verifyOtp(next.join(""));
    }
  }

  function handleOtpKeyDown(index, e) {
    if (e.key === "Backspace" && !otpDigits[index] && index > 0) {
      setOtpDigits((prev) => {
        const next = [...prev];
        next[index - 1] = "";
        return next;
      });
      focusOtpBox(index - 1);
    }
  }

  if (showPromo) {
    return (
      <div className="client-login-overlay" onMouseDown={onClose}>
        <div className="app-promo-modal" role="dialog" aria-modal="true" onMouseDown={(e) => e.stopPropagation()}>
          <AppPromoContent onClose={onClose} onContinue={() => setShowPromo(false)} />
        </div>
      </div>
    );
  }

  return (
    <div className="client-login-overlay" onMouseDown={onClose}>
      <div className="client-login-modal" onMouseDown={(e) => e.stopPropagation()}>
        <button type="button" className="client-login-close" aria-label={t("common:close")} onClick={onClose}>
          &times;
        </button>

        <h2 className="client-login-title">{step === "phone" ? t("common:logIn") : t("otpTitle")}</h2>
        <p className="client-login-subtitle">
          {step === "phone"
            ? t("phoneSubtitle")
            : t("otpSubtitle", { phone: `+${callingCode}${localNumber}` })}
        </p>

        {step === "phone" ? (
          <form onSubmit={sendOtp}>
            <div className="client-login-country-row">
              {PHONE_OPTIONS.map((option) => (
                <button
                  type="button"
                  key={option.id}
                  className={`client-login-country ${phoneOptionId === option.id ? "active" : ""}`}
                  onClick={() => setPhoneOptionId(option.id)}
                  title={t(option.labelKey)}
                >
                  <CountryFlag country={option.country} />
                  <span dir="ltr">+{option.callingCode}</span>
                </button>
              ))}
            </div>

            <div className="client-login-phone-row">
              <span className="client-login-calling-code">+{callingCode}</span>
              <input
                type="tel"
                className="client-login-phone-input"
                placeholder={t("phoneNumberPlaceholder")}
                value={localNumber}
                onChange={(e) => setLocalNumber(e.target.value)}
                maxLength={15}
                autoFocus
              />
            </div>

            {error && <p className="client-login-error">{error}</p>}

            <button type="submit" className="client-login-button" disabled={loading || !isPhoneValid}>
              {loading ? t("sendingButton") : t("sendCodeButton")}
            </button>

            {isDevEnvironment && (
              <button
                type="button"
                className="client-login-dev-button"
                onClick={continueAsDev}
                disabled={devLoading}
              >
                {devLoading ? t("signingInButton") : t("devSkipOtpButton")}
              </button>
            )}
          </form>
        ) : (
          <>
            <div className="client-login-otp-row">
              {otpDigits.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (otpRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  className={`client-login-otp-box ${digit ? "filled" : ""}`}
                  value={digit}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(index, e)}
                  maxLength={OTP_LENGTH}
                  autoFocus={index === 0}
                />
              ))}
            </div>

            {error && <p className="client-login-error">{error}</p>}

            <button
              type="button"
              className="client-login-button"
              onClick={() => verifyOtp()}
              disabled={loading || code.length < OTP_LENGTH}
            >
              {loading ? t("verifyingButton") : t("verifyContinueButton")}
            </button>

            <div className="client-login-footer-row">
              <button
                type="button"
                className="client-login-link"
                onClick={sendOtp}
                disabled={loading || cooldown > 0}
              >
                {cooldown > 0 ? t("resendCodeIn", { seconds: cooldown }) : t("resendCode")}
              </button>
              <button type="button" className="client-login-link" onClick={backToPhone} disabled={loading}>
                {t("changeNumber")}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
